'use client'

import { memo, useCallback, useEffect, useRef, useState } from 'react'
import { useReducedMotion } from 'framer-motion'
import { cn } from '@/lib/utils'

const TAGLINE = "Navigating India's Next."
const BAR_COLOR = '#88a1ac33'
const BALL_COLOR = '#88a1ac66'

/** baby: First letter of each word is invincible: leaves N·I·N standing */
const PROTECTED_LETTER_INDICES: Set<number> = (() => {
  const protectedIdx = new Set<number>()
  let letterIndex = 0
  let atWordStart = true
  for (const char of TAGLINE) {
    if (char === ' ') {
      atWordStart = true
      continue
    }
    if (atWordStart) {
      protectedIdx.add(letterIndex)
      atWordStart = false
    }
    letterIndex += 1
  }
  return protectedIdx
})()

const TOTAL_LETTER_COUNT = TAGLINE.replace(/ /g, '').length
const HITTABLE_LETTER_COUNT = TOTAL_LETTER_COUNT - PROTECTED_LETTER_INDICES.size
/** baby: Pause on N·I·N, then restore the full tagline */
const RESET_AFTER_MS = 19800

interface Ball {
  x: number
  y: number
  dx: number
  dy: number
  radius: number
}

interface Paddle {
  x: number
  y: number
  width: number
  height: number
  target: number
  isVertical: boolean
}

interface LetterBox {
  index: number
  x: number
  y: number
  w: number
  h: number
}

/**
 * Exact heading typography (ninxl-8bit-heading) with subtle Pong bars/ball.
 * Only hit letters fade to a light block: spacing/size match the old h1.
 */
export const NinTaglinePong = memo(function NinTaglinePong({
  headingAs = 'h1'
}: {
  headingAs?: 'h1' | 'p'
}) {
  const Heading = headingAs
  const wrapRef = useRef<HTMLDivElement>(null)
  const canvasRef = useRef<HTMLCanvasElement>(null)
  const letterRefs = useRef<Map<number, HTMLSpanElement>>(new Map())
  const ballRef = useRef<Ball>({ x: 0, y: 0, dx: 0, dy: 0, radius: 0 })
  const paddlesRef = useRef<Paddle[]>([])
  const boxesRef = useRef<LetterBox[]>([])
  const rafRef = useRef(0)
  const lastFrameRef = useRef(0)
  const sizeRef = useRef({ width: 1, height: 1 })
  const resetTimerRef = useRef(0)
  const resetScheduledRef = useRef(false)
  const [hit, setHit] = useState<Set<number>>(() => new Set())
  const hitRef = useRef<Set<number>>(new Set())
  const shouldReduceMotion = useReducedMotion()

  const setLetterRef = useCallback(
    (index: number, el: HTMLSpanElement | null) => {
      if (el) letterRefs.current.set(index, el)
      else letterRefs.current.delete(index)
    },
    []
  )

  useEffect(() => {
    if (shouldReduceMotion || process.env.NODE_ENV === 'test') return

    const wrap = wrapRef.current
    const canvas = canvasRef.current
    if (!wrap || !canvas) return

    const ctx = canvas.getContext('2d')
    if (!ctx) return

    const resetTagline = () => {
      hitRef.current = new Set()
      resetScheduledRef.current = false
      setHit(new Set())
      // baby: Remeasure after paint so hittable boxes return
      window.requestAnimationFrame(() => {
        measureLetters()
      })
    }

    const scheduleResetIfDone = () => {
      if (resetScheduledRef.current) return
      if (hitRef.current.size < HITTABLE_LETTER_COUNT) return
      resetScheduledRef.current = true
      resetTimerRef.current = window.setTimeout(resetTagline, RESET_AFTER_MS)
    }

    const measureLetters = () => {
      const wrapRect = wrap.getBoundingClientRect()
      const boxes: LetterBox[] = []
      letterRefs.current.forEach((el, index) => {
        // baby: Skip protected first-of-word letters (NIN remains)
        if (PROTECTED_LETTER_INDICES.has(index)) return
        if (hitRef.current.has(index)) return
        const r = el.getBoundingClientRect()
        boxes.push({
          index,
          x: r.left - wrapRect.left,
          y: r.top - wrapRect.top,
          w: r.width,
          h: r.height
        })
      })
      boxesRef.current = boxes
    }

    const initPhysics = (width: number, height: number) => {
      // baby: Bars/ball scale to compact Press Start size (clamp ~0.7-1.05rem)
      const bar = Math.max(2, Math.round(height * 0.028))
      const ballR = Math.max(2.5, height * 0.032)
      // baby: Shorter paddles on a taller field → more travel + steeper bounce angles
      const paddleLen = Math.max(18, Math.min(42, height * 0.24))
      const speed = 1

      ballRef.current = {
        x: width * 0.82,
        y: height * 0.22,
        dx: -speed,
        dy: speed * 1.05,
        radius: ballR
      }

      paddlesRef.current = [
        {
          x: 0,
          y: height / 2 - paddleLen / 2,
          width: bar,
          height: paddleLen,
          target: height / 2,
          isVertical: true
        },
        {
          x: width - bar,
          y: height / 2 - paddleLen / 2,
          width: bar,
          height: paddleLen,
          target: height / 2,
          isVertical: true
        },
        {
          x: width / 2 - paddleLen / 2,
          y: 0,
          width: paddleLen,
          height: bar,
          target: width / 2,
          isVertical: false
        },
        {
          x: width / 2 - paddleLen / 2,
          y: height - bar,
          width: paddleLen,
          height: bar,
          target: width / 2,
          isVertical: false
        }
      ]
    }

    const resize = () => {
      const rect = wrap.getBoundingClientRect()
      const width = Math.max(1, Math.floor(rect.width))
      const height = Math.max(1, Math.floor(rect.height))
      sizeRef.current = { width, height }
      const dpr = Math.min(window.devicePixelRatio || 1, 2)
      canvas.width = width * dpr
      canvas.height = height * dpr
      canvas.style.width = `${width}px`
      canvas.style.height = `${height}px`
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0)
      initPhysics(width, height)
      measureLetters()
    }

    const update = (width: number, height: number, frameScale: number) => {
      const ball = ballRef.current
      const paddles = paddlesRef.current

      ball.x += ball.dx * frameScale
      ball.y += ball.dy * frameScale

      if (ball.y - ball.radius < 0 || ball.y + ball.radius > height) {
        ball.dy = -ball.dy
        ball.y = Math.max(ball.radius, Math.min(height - ball.radius, ball.y))
      }
      if (ball.x - ball.radius < 0 || ball.x + ball.radius > width) {
        ball.dx = -ball.dx
        ball.x = Math.max(ball.radius, Math.min(width - ball.radius, ball.x))
      }

      paddles.forEach(paddle => {
        if (paddle.isVertical) {
          if (
            ball.x - ball.radius < paddle.x + paddle.width &&
            ball.x + ball.radius > paddle.x &&
            ball.y > paddle.y &&
            ball.y < paddle.y + paddle.height
          ) {
            ball.dx = -ball.dx
          }
          paddle.target = ball.y - paddle.height / 2
          paddle.target = Math.max(
            0,
            Math.min(height - paddle.height, paddle.target)
          )
          paddle.y +=
            (paddle.target - paddle.y) * Math.min(0.18, 0.1 * frameScale)
        } else {
          if (
            ball.y - ball.radius < paddle.y + paddle.height &&
            ball.y + ball.radius > paddle.y &&
            ball.x > paddle.x &&
            ball.x < paddle.x + paddle.width
          ) {
            ball.dy = -ball.dy
          }
          paddle.target = ball.x - paddle.width / 2
          paddle.target = Math.max(
            0,
            Math.min(width - paddle.width, paddle.target)
          )
          paddle.x +=
            (paddle.target - paddle.x) * Math.min(0.18, 0.1 * frameScale)
        }
      })

      // baby: Hit real letter boxes: only then turn that glyph light
      let changed = false
      boxesRef.current.forEach(box => {
        if (hitRef.current.has(box.index)) return
        if (
          ball.x + ball.radius > box.x &&
          ball.x - ball.radius < box.x + box.w &&
          ball.y + ball.radius > box.y &&
          ball.y - ball.radius < box.y + box.h
        ) {
          hitRef.current.add(box.index)
          changed = true
          const cx = box.x + box.w / 2
          const cy = box.y + box.h / 2
          if (Math.abs(ball.x - cx) > Math.abs(ball.y - cy)) ball.dx = -ball.dx
          else ball.dy = -ball.dy
        }
      })
      if (changed) {
        setHit(new Set(hitRef.current))
        measureLetters()
        scheduleResetIfDone()
      }
    }

    const draw = (width: number, height: number) => {
      ctx.clearRect(0, 0, width, height)

      ctx.fillStyle = BAR_COLOR
      paddlesRef.current.forEach(paddle => {
        ctx.fillRect(paddle.x, paddle.y, paddle.width, paddle.height)
      })

      ctx.fillStyle = BALL_COLOR
      ctx.beginPath()
      ctx.arc(
        ballRef.current.x,
        ballRef.current.y,
        ballRef.current.radius,
        0,
        Math.PI * 2
      )
      ctx.fill()
    }

    const loop = (now: number) => {
      rafRef.current = 0
      const { width, height } = sizeRef.current
      const elapsed = lastFrameRef.current ? now - lastFrameRef.current : 16.67
      const frameScale = Math.min(2, elapsed / 16.67)
      lastFrameRef.current = now
      update(width, height, frameScale)
      draw(width, height)
      rafRef.current = requestAnimationFrame(loop)
    }

    const syncPlayback = () => {
      const shouldRun =
        document.visibilityState === 'visible' && window.scrollY < 360
      if (shouldRun && rafRef.current === 0) {
        lastFrameRef.current = performance.now()
        rafRef.current = requestAnimationFrame(loop)
      } else if (!shouldRun && rafRef.current !== 0) {
        cancelAnimationFrame(rafRef.current)
        rafRef.current = 0
      }
    }

    resize()
    const observer = new ResizeObserver(resize)
    observer.observe(wrap)
    // baby: Remeasure after fonts settle
    const fontTimer = window.setTimeout(measureLetters, 100)
    window.addEventListener('scroll', syncPlayback, { passive: true })
    document.addEventListener('visibilitychange', syncPlayback)
    syncPlayback()

    return () => {
      observer.disconnect()
      cancelAnimationFrame(rafRef.current)
      rafRef.current = 0
      window.removeEventListener('scroll', syncPlayback)
      document.removeEventListener('visibilitychange', syncPlayback)
      window.clearTimeout(fontTimer)
      window.clearTimeout(resetTimerRef.current)
    }
  }, [shouldReduceMotion])

  let letterIndex = 0

  return (
    // baby: Shrink-wrap to the tagline so right paddle sits at the text end (not far right).
    <div className="relative mx-auto mb-6 mt-3 inline-block w-fit max-w-full">
      {/* baby: Playfield only slightly wider than glyphs: bars hug the text edges */}
      <div
        ref={wrapRef}
        className="pointer-events-none absolute left-1/2 top-1/2 z-0 h-[calc(100%+5rem)] w-[calc(100%+0.75rem)] -translate-x-1/2 -translate-y-1/2 sm:h-[calc(100%+6rem)] md:h-[calc(100%+4rem)]"
      >
        <canvas
          ref={canvasRef}
          className="absolute inset-0 h-full w-full"
          aria-hidden
        />
      </div>
      <Heading
        className={cn(
          'nin-tagline-text ninxl-8bit-heading ninxl-theme-text relative z-10 leading-relaxed'
        )}
      >
        {TAGLINE.split('').map((char, i) => {
          if (char === ' ') {
            return <span key={`sp-${i}`}> </span>
          }
          const index = letterIndex
          letterIndex += 1
          const isProtected = PROTECTED_LETTER_INDICES.has(index)
          const isHit = !isProtected && hit.has(index)
          return (
            <span
              key={`ch-${index}`}
              ref={el => setLetterRef(index, el)}
              className={cn(
                'inline-block transition-colors duration-150',
                isHit && 'text-[#88a1ac33]'
              )}
              data-protected={isProtected ? 'true' : undefined}
            >
              {char}
            </span>
          )
        })}
      </Heading>
    </div>
  )
})
